import { useCallback, useEffect, useState } from "react";
import type React from "react";
import { createPortal } from "react-dom";

export type ToastKind = "error" | "success" | "info";

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

/**
 * A module-level pub-sub rather than only a context, because the loudest
 * error source — the QueryClient's global `onError` in main.tsx — fires
 * outside any component and above `ToastProvider` in the tree. A function
 * either side can call is the only shape that reaches both.
 */
type Listener = (toast: { kind: ToastKind; message: string }) => void;
const listeners = new Set<Listener>();
let nextId = 1;

export function notify(kind: ToastKind, message: string) {
  for (const listener of listeners) listener({ kind, message });
}

export function notifyError(message: string) {
  notify("error", message);
}

export function notifySuccess(message: string) {
  notify("success", message);
}

/**
 * Mounted once, at the root. Renders through a portal so a toast sits above
 * everything else on screen — including a `Modal`'s own dialog layer — and
 * survives regardless of where in the tree the error originated.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  useEffect(() => {
    const listener: Listener = ({ kind, message }) => {
      const id = nextId++;
      setToasts((t) => [...t, { id, kind, message }]);
      // Errors sit longer than a success confirmation — a mistake is worth
      // more of a person's attention than a "saved" they already expected.
      const ttl = kind === "error" ? 8000 : 4500;
      setTimeout(() => dismiss(id), ttl);
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [dismiss]);

  return (
    <>
      {children}
      {createPortal(
        <div
          aria-live="assertive"
          className="pointer-events-none fixed inset-x-0 top-4 z-[1000] flex flex-col items-center gap-2 px-4 sm:left-auto sm:right-4 sm:items-end"
        >
          {toasts.map((t) => (
            <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const styles =
    toast.kind === "error"
      ? "border-red-200 bg-red-50 text-red-800"
      : toast.kind === "success"
        ? "border-green-200 bg-green-50 text-green-800"
        : "border-neutral-200 bg-white text-neutral-800";
  return (
    <div
      role="alert"
      className={`pointer-events-auto w-full max-w-sm rounded-xl border px-4 py-3 text-sm shadow-lg ${styles}`}
    >
      <div className="flex items-start gap-3">
        <p className="flex-1">{toast.message}</p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 text-current opacity-60 transition-opacity hover:opacity-100"
        >
          ×
        </button>
      </div>
    </div>
  );
}
